import {
  Component,
  computed,
  effect,
  inject,
  ViewChild,
  TemplateRef,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { EquipmentStore } from '@features/catalog/store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
} from '@shared/components/data-table/data-table';
import { SolarModule } from '@features/catalog/models';
import { CreateModuleDialog } from '@shared/components/create-module-dialog/create-module-dialog';

@Component({
  selector: 'app-modules-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DataTableComponent,
    CreateModuleDialog,
  ],
  templateUrl: './modules-list.html',
  styleUrl: './modules-list.css',
})
export class ModulesListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly linkedEquipment = computed(() => this.queryParams().get('equipment'));
  readonly visibleEquipment = computed(() => {
    const id = this.linkedEquipment();
    const items = this.equipmentStore.allModules();
    return id ? items.filter(item => item.id === id) : items;
  });
  private readonly equipmentStore = inject(EquipmentStore);
  readonly currentOrgStore = inject(CurrentOrganizationStore);

  // Expose console for debugging in templates
  console = console;

  @ViewChild('createDialog') createDialog!: CreateModuleDialog;
  @ViewChild('moduleTpl', { static: true }) moduleTpl!: TemplateRef<any>;
  @ViewChild('performanceTpl', { static: true })
  performanceTpl!: TemplateRef<any>;
  @ViewChild('sizeTpl', { static: true }) sizeTpl!: TemplateRef<any>;
  @ViewChild('efficiencyTpl', { static: true })
  efficiencyTpl!: TemplateRef<any>;
  @ViewChild('costsTpl', { static: true }) costsTpl!: TemplateRef<any>;

  // State for edit mode
  editingModule = signal<SolarModule | null>(null);

  organizationId = computed(() => {
    const id = this.currentOrgStore.organizationId() || '';
    console.log('[ModulesList] organizationId computed:', id);
    return id;
  });

  modules = computed(() => {
    const mods = this.equipmentStore.allModules();
    console.log('[ModulesList] modules computed:', mods, 'count:', mods.length);
    mods.forEach((m, idx) => {
      console.log(`  [${idx}] full object:`, JSON.stringify(m, null, 2));
      console.log(`  [${idx}] keys:`, Object.keys(m));
    });
    return mods;
  });

  isLoading = computed(() => {
    const loading = this.equipmentStore.isLoading();
    console.log('[ModulesList] isLoading computed:', loading);
    return loading;
  });

  columns: ColumnDef<SolarModule>[] = [];

  actions: TableAction<SolarModule>[] = [
    {
      label: 'Bearbeiten',
      action: (item) => this.editModule(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'update'),
    },
    {
      label: 'Löschen',
      type: 'destructive',
      action: (item) => this.deleteModule(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'delete'),
      dividerBefore: true,
    },
  ];

  ngOnInit() {
    this.columns = [
      {
        header: 'Modul',
        template: this.moduleTpl,
      },
      {
        header: 'Leistung',
        template: this.performanceTpl,
        class: 'w-[120px] text-right',
      },
      {
        header: 'Größe',
        template: this.sizeTpl,
      },
      {
        header: 'Leistungsverlust',
        template: this.efficiencyTpl,
        class: 'w-[150px] text-right',
      },
      {
        header: 'Kosten',
        template: this.costsTpl,
        class: 'w-[140px] text-right',
      },
    ];
  }

  constructor() {
    // Legitimate side-effect use: triggers a store load (network I/O) whenever
    // the active organisation changes. This is not state derivation — it is an
    // imperative command to fetch data. No cleaner alternative exists here because
    // the load must re-fire reactively on orgId changes without an Observable pipeline.
    effect(() => {
      const orgId = this.organizationId();
      console.log('[ModulesList] effect triggered, orgId:', orgId);
      if (orgId) {
        console.log(
          '[ModulesList] calling equipmentStore.loadModules with orgId:',
          orgId
        );
        this.equipmentStore.loadModules(orgId);
      } else {
        console.log('[ModulesList] orgId is empty, skipping loadModules');
      }
    });
  }

  async reloadModules() {
    const orgId = this.organizationId();
    if (orgId) {
      this.equipmentStore.loadModules(orgId);
    }
  }

  async syncModules() {
    try {
      const orgId = this.organizationId();
      if (orgId) {
        await this.equipmentStore.syncModules(orgId);
      }
    } catch (error) {
      console.error('Error syncing modules:', error);
    }
  }

  addModule() {
    console.log(
      '[ModulesList] addModule called, createDialog exists:',
      !!this.createDialog
    );
    this.editingModule.set(null);
    this.createDialog?.open();
  }

  editModule(module: SolarModule) {
    console.log('Edit module:', module);
    this.editingModule.set(module);
    this.createDialog?.open();
  }

  async deleteModule(module: SolarModule) {
    if (confirm(`Möchten Sie das Modul "${module.name}" wirklich löschen?`)) {
      try {
        const orgId = this.organizationId();
        if (orgId) {
          await this.equipmentStore.deleteModule(orgId, module.id);
        }
      } catch (error) {
        console.error('Error deleting module:', error);
      }
    }
  }
}
